import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  usePharmacySuppliers,
  usePharmacyDrugs,
  useCreatePharmacyPurchaseOrder,
} from '../../services/httpServices/queries';
import { Plus, Trash2, Loader2, ArrowLeft, ShoppingCart } from 'lucide-react';

interface LineItem {
  drugId: string;
  orderedQuantity: number;
  unitCost: number;
  batchNumber: string;
  expiryDate: string;
}

const emptyLine: LineItem = {
  drugId: '',
  orderedQuantity: 1,
  unitCost: 0,
  batchNumber: '',
  expiryDate: '',
};

export function PharmacyPurchaseForm() {
  const navigate = useNavigate();
  const { data: suppliersData } = usePharmacySuppliers();
  const { data: drugsData } = usePharmacyDrugs(1, 200);
  const createMutation = useCreatePharmacyPurchaseOrder();

  const suppliers = suppliersData?.data || [];
  const drugs = drugsData?.data || [];

  const [supplierId, setSupplierId] = useState('');
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [discount, setDiscount] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([{ ...emptyLine }]);

  const subtotal = useMemo(
    () => lines.reduce((sum, l) => sum + Number(l.orderedQuantity) * Number(l.unitCost), 0),
    [lines],
  );
  const total = subtotal - Number(discount || 0) + Number(taxAmount || 0);

  const updateLine = (index: number, patch: Partial<LineItem>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validLines = lines.filter((l) => l.drugId && Number(l.orderedQuantity) > 0);
    if (!supplierId || validLines.length === 0) return;

    await createMutation.mutateAsync({
      supplierId,
      orderDate: orderDate || undefined,
      expectedDelivery: expectedDelivery || undefined,
      discount: Number(discount) || 0,
      taxAmount: Number(taxAmount) || 0,
      notes: notes || undefined,
      items: validLines.map((l) => ({
        drugId: l.drugId,
        orderedQuantity: Number(l.orderedQuantity),
        unitCost: Number(l.unitCost),
        batchNumber: l.batchNumber || undefined,
        expiryDate: l.expiryDate || undefined,
      })),
    });
    navigate('/pharmacy/purchases');
  };

  const inputClass =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <PharmacyLayout title="New Purchase Order" description="Create a purchase order to a pharmacy supplier">
      <form onSubmit={submit} className="space-y-4">
        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Supplier *</label>
              <select required value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputClass}>
                <option value="">Select supplier</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Order Date</label>
              <DatePicker value={orderDate} onChange={setOrderDate} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Expected Delivery</label>
              <DatePicker value={expectedDelivery} onChange={setExpectedDelivery} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-text mb-1">Invoice Number</label>
              <input disabled placeholder="Added on receipt" className={`${inputClass} opacity-60`} />
            </div>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <ShoppingCart size={16} className="text-primary" />
              <span className="font-medium text-text">Items</span>
            </div>
            <button
              type="button"
              onClick={() => setLines((prev) => [...prev, { ...emptyLine }])}
              className="px-3 py-1.5 bg-primary/10 text-primary rounded-lg text-sm font-medium hover:bg-primary/20 flex items-center gap-1.5"
            >
              <Plus size={14} /> Add Item
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Drug</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Unit Cost</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Line Total</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Batch</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Expiry</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 min-w-[220px]">
                      <select
                        value={line.drugId}
                        onChange={(e) => updateLine(index, { drugId: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">Select drug</option>
                        {drugs.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 w-24">
                      <input
                        type="number"
                        min={1}
                        value={line.orderedQuantity}
                        onChange={(e) => updateLine(index, { orderedQuantity: Number(e.target.value) })}
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 w-32">
                      <ValidatedNumberInput
                        value={line.unitCost}
                        onChange={(v) => updateLine(index, { unitCost: v })}
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-text w-32">
                      {(Number(line.orderedQuantity) * Number(line.unitCost)).toFixed(2)}
                    </td>
                    <td className="px-3 py-2 w-36">
                      <input
                        value={line.batchNumber}
                        onChange={(e) => updateLine(index, { batchNumber: e.target.value })}
                        placeholder="Optional"
                        className={inputClass}
                      />
                    </td>
                    <td className="px-3 py-2 w-40">
                      <DatePicker
                        value={line.expiryDate}
                        onChange={(value) => updateLine(index, { expiryDate: value })}
                        className={inputClass}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))}
                        className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-background"
                        title="Remove"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Notes</label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputClass}
                placeholder="Optional notes for this order"
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-muted">Subtotal</span>
              <span className="font-medium text-text">{subtotal.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between text-sm gap-3">
              <span className="text-text-muted">Discount</span>
              <ValidatedNumberInput
                value={discount}
                onChange={setDiscount}
                className={`${inputClass} w-32 text-right`}
              />
            </div>
            <div className="flex items-center justify-between text-sm gap-3">
              <span className="text-text-muted">Tax</span>
              <ValidatedNumberInput
                value={taxAmount}
                onChange={setTaxAmount}
                className={`${inputClass} w-32 text-right`}
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <span className="font-medium text-text">Total</span>
              <span className="text-lg font-bold text-primary">{total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/pharmacy/purchases')}
            className="px-4 py-2 text-sm text-text-muted hover:text-text flex items-center gap-1.5"
          >
            <ArrowLeft size={16} /> Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending || !supplierId}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {createMutation.isPending && <Loader2 size={16} className="animate-spin" />}
            Create Purchase Order
          </button>
        </div>
      </form>
    </PharmacyLayout>
  );
}
